import { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

export default function Wholesale() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    shop_name: "",
    contact_name: "",
    email: "",
    phone: "",
    website: "",
    location: "",
    description: "",
  });

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.description.trim().length < 10) {
      toast.error("Tell us a bit more about the shop.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/wholesale", {
        shop_name: form.shop_name.trim(),
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        website: form.website.trim() || null,
        location: form.location.trim(),
        description: form.description.trim(),
      });
      setDone(true);
      toast.success("Enquiry received. We'll be in touch.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send enquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="wholesale-page" className="bg-[#F9F9F4] text-[#1A3626]">
      <SEO
        title="Wholesale"
        path="/wholesale"
        description="Wet Cabbage wholesale enquiries. Premium streetwear documenting modern leisure behaviour — for shops, stockists and curators with strong opinions about pub carpets."
      />

      <section className="bg-[#F9F9F4]" data-testid="wholesale-masthead">
        <div className="mx-auto max-w-[1200px] px-6 pb-10 pt-20 md:px-10 md:pb-14 md:pt-28">
          <div className="font-display text-[10px] font-black uppercase tracking-[0.42em] text-[#93C065] md:text-xs">
            Wholesale &mdash; Stockists
          </div>
          <h1 className="font-display mt-5 max-w-[14ch] text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl md:text-7xl lg:text-8xl">
            Stock the universe.
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#1A3626] md:text-xl md:leading-[1.6]">
            We work with a small, carefully picked group of independent shops,
            concept stores and curators who understand that ordinary life
            deserves better clothing.
            <br /><br />
            If that&rsquo;s your shop, send us a note.
          </p>
        </div>
      </section>

      <section
        className="border-y-2 border-[#1A3626] bg-[#1A3626] py-12 text-[#F9F9F4] md:py-16"
        data-testid="wholesale-pillars"
      >
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-10 px-6 md:grid-cols-3 md:gap-12 md:px-10">
          <Pillar
            n="01"
            title="Made to order"
            body="No deadstock politics. We print on demand and ship to your door without warehouse drama."
          />
          <Pillar
            n="02"
            title="Premium garments"
            body="Heavyweight blanks. Properly photographed. Designed to look good on a sales floor."
          />
          <Pillar
            n="03"
            title="Honest margins"
            body="Wholesale terms that work for small shops. No 200-piece minimums. No deeply boring contracts."
          />
        </div>
      </section>

      <section
        id="enquire"
        className="bg-[#EBE7DD] py-20 md:py-28"
        data-testid="wholesale-form-section"
      >
        <div className="mx-auto max-w-[760px] px-6 md:px-10">
          <div className="font-display text-[10px] font-black uppercase tracking-[0.42em] text-[#1A3626]/65 md:text-xs">
            Wholesale enquiry
          </div>
          <h2 className="font-display mt-4 text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl md:text-6xl">
            Tell us about your shop.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#1A3626]/85 md:text-lg">
            One curator and a dog named Mush will read every enquiry. Be honest
            and a bit weird. The shops we like best usually are.
          </p>

          {done ? (
            <div
              className="mt-10 border-2 border-[#1A3626] bg-[#F9F9F4] p-8 md:p-10"
              data-testid="wholesale-success"
            >
              <CheckCircle2 className="h-10 w-10 text-[#93C065]" strokeWidth={2.4} />
              <h3 className="font-display mt-4 text-2xl font-black uppercase tracking-tight md:text-3xl">
                Enquiry received.
              </h3>
              <p className="mt-3 text-base leading-relaxed text-[#1A3626]/85 md:text-lg">
                We&rsquo;ll be in touch within a week. Possibly sooner. Mush is
                very busy but takes wholesale enquiries seriously.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="mt-10 grid grid-cols-1 gap-6"
              data-testid="wholesale-form"
            >
              <Field label="Shop name" required>
                <input
                  type="text"
                  required
                  maxLength={200}
                  value={form.shop_name}
                  onChange={onChange("shop_name")}
                  placeholder="e.g. Northfield Goods"
                  className="form-input"
                  data-testid="ws-input-shop-name"
                />
              </Field>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Field label="Your name" required>
                  <input
                    type="text"
                    required
                    maxLength={120}
                    value={form.contact_name}
                    onChange={onChange("contact_name")}
                    placeholder="e.g. Dave"
                    className="form-input"
                    data-testid="ws-input-contact-name"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="dave@example.com"
                    className="form-input"
                    data-testid="ws-input-email"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Field label="Phone (optional)">
                  <input
                    type="tel"
                    maxLength={40}
                    value={form.phone}
                    onChange={onChange("phone")}
                    placeholder="+44 7000 000000"
                    className="form-input"
                    data-testid="ws-input-phone"
                  />
                </Field>
                <Field label="Shop website or Instagram (optional)">
                  <input
                    type="text"
                    maxLength={300}
                    value={form.website}
                    onChange={onChange("website")}
                    placeholder="northfieldgoods.com / @northfieldgoods"
                    className="form-input"
                    data-testid="ws-input-website"
                  />
                </Field>
              </div>

              <Field label="Location" required>
                <input
                  type="text"
                  required
                  maxLength={200}
                  value={form.location}
                  onChange={onChange("location")}
                  placeholder="City, country (e.g. Glasgow, UK)"
                  className="form-input"
                  data-testid="ws-input-location"
                />
              </Field>

              <Field
                label="About your shop"
                required
                helper="A short paragraph. What you stock, who shops there, why Wet Cabbage fits in."
              >
                <textarea
                  required
                  minLength={10}
                  maxLength={2500}
                  value={form.description}
                  onChange={onChange("description")}
                  rows={6}
                  placeholder="We're an independent menswear shop in a converted petrol station…"
                  className="form-input"
                  data-testid="ws-input-description"
                />
              </Field>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="font-display inline-flex items-center gap-3 border-2 border-[#1A3626] bg-[#1A3626] px-7 py-4 text-xs font-black uppercase tracking-[0.32em] text-[#F9F9F4] transition-all hover:-translate-y-0.5 hover:bg-transparent hover:text-[#1A3626] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 md:px-9 md:py-5 md:text-sm md:tracking-[0.36em]"
                  data-testid="ws-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />
                      Sending&hellip;
                    </>
                  ) : (
                    <>
                      Send enquiry
                      <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                    </>
                  )}
                </button>
                <span className="text-xs text-[#1A3626]/65 md:text-sm">
                  No bots. Just curators and a supervisor dog.
                </span>
              </div>
            </form>
          )}
        </div>
      </section>

      <style>{`
        .form-input{
          width:100%;background:#F9F9F4;border:2px solid #1A3626;
          padding:14px 16px;font-size:16px;color:#1A3626;font-family:inherit;
          outline:none;transition:box-shadow 150ms ease, transform 150ms ease;
        }
        .form-input:focus{ box-shadow: 4px 4px 0 0 #93C065; transform: translate(-1px,-1px); }
        textarea.form-input{ resize: vertical; min-height: 140px; line-height: 1.55; }
      `}</style>
    </div>
  );
}

function Field({ label, helper, required, children }) {
  return (
    <label className="block">
      <span className="font-display block text-[10px] font-bold uppercase tracking-[0.32em] text-[#1A3626] md:text-xs">
        {label} {required && <span className="text-[#93C065]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
      {helper && (
        <span className="mt-2 block text-xs leading-relaxed text-[#1A3626]/65">
          {helper}
        </span>
      )}
    </label>
  );
}

function Pillar({ n, title, body }) {
  return (
    <div className="border-t-2 border-[#F9F9F4]/30 pt-6">
      <div className="font-display text-[10px] font-black uppercase tracking-[0.42em] text-[#93C065] md:text-xs">
        {n}
      </div>
      <h3 className="font-display mt-3 text-2xl font-black uppercase tracking-tight md:text-3xl">
        {title}
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-[#F9F9F4]/85 md:text-base md:leading-[1.65]">
        {body}
      </p>
    </div>
  );
}
